import React, { useEffect, useMemo, useRef, useState, useContext } from "react";
import axios from "axios";
import { AuthContext } from "../../contexts/AuthContext";
import { tempUrl } from "../../contexts/ContextProvider";
import { Alert, Badge, Button, Card, Col, Container, Dropdown, DropdownButton, Form, Modal, Nav, Pagination, Row, Spinner, Table } from "react-bootstrap";

const number = (value, digits = 2) => Number(value || 0).toLocaleString("id-ID", { maximumFractionDigits: Math.min(digits, 3) });
const money = (value) => `Rp ${number(value, 2)}`;
const listData = (value) => (Array.isArray(value) ? value : value?.data || []);
const pageData = (value) => ({ rows: listData(value), page: Number(value?.page || 0), totalPage: Math.max(Number(value?.totalPage || 0), 1), totalRows: Number(value?.totalRows || 0) });
const branchId = (user) => user?.cabangId || user?.cabang?.id || "";

const parseNumericInput = (value) => {
  const text = String(value ?? "").trim().replace(/\s/g, "");
  if (!text) return "";
  if (text.includes(",")) return text.replace(/\./g, "").replace(",", ".").replace(/[^0-9.-]/g, "");
  const dotCount = (text.match(/\./g) || []).length;
  if (dotCount > 1 || (dotCount === 1 && /^-?\d+\.\d{3}$/.test(text))) return text.replace(/\./g, "").replace(/[^0-9-]/g, "");
  return text.replace(/[^0-9.-]/g, "");
};

// The displayed value already contains Indonesian thousands separators. During
// typing, every dot is therefore a grouping separator; only a comma starts a
// decimal part. This prevents a re-render such as `2.6000` from being treated
// as decimal `2.6000` and displayed incorrectly as `2,60`.
const parseEditedNumericInput = (value) => {
  const text = String(value ?? "").trim().replace(/\s/g, "");
  if (!text) return "";
  if (text.includes(",")) return text.replace(/\./g, "").replace(",", ".").replace(/[^0-9.-]/g, "");
  return text.replace(/\./g, "").replace(/[^0-9-]/g, "");
};

const formatNumericInput = (value, decimalDigits = 3) => {
  const raw = parseNumericInput(value);
  if (!raw || raw === "-") return raw;
  const [integerPart = "", fractionPart] = raw.split(".");
  const sign = integerPart.startsWith("-") ? "-" : "";
  const integerDigits = integerPart.replace(/^-/, "").replace(/\D/g, "") || "0";
  const grouped = integerDigits.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  const fraction = fractionPart === undefined ? "" : fractionPart.replace(/\D/g, "").slice(0, decimalDigits).replace(/0+$/, "");
  return `${sign}${grouped}${fraction ? `,${fraction}` : ""}`;
};

const limitNumericPrecision = (value, decimalDigits) => {
  const raw = parseNumericInput(value);
  if (!raw || raw === "-" || !raw.includes(".")) return raw;
  const [integerPart, fractionPart = ""] = raw.split(".");
  return `${integerPart}.${fractionPart.slice(0, decimalDigits)}`;
};

const NumericInput = ({ value, onChange, decimalDigits = 3, ...props }) => {
  const precision = Math.min(decimalDigits, 3);
  return <Form.Control
    {...props}
    type="text"
    inputMode="decimal"
    value={formatNumericInput(value, precision)}
    onChange={(event) => onChange(limitNumericPrecision(parseEditedNumericInput(event.target.value), precision))}
  />;
};

const usePosApi = () => {
  const { user } = useContext(AuthContext);
  const call = async (path, body = {}) => {
    const response = await axios.post(`${tempUrl}${path}`, {
      ...body,
      _id: user.id,
      token: user.token,
    });
    return response.data;
  };
  return { user, call };
};

const PageFrame = ({ eyebrow = "POS", title, children, actions, className = "" }) => (
  <Container className={`py-4 ${eyebrow.startsWith("POS") ? "pos-page-frame" : ""} ${className}`}>
    <div className="d-flex justify-content-between align-items-end border-bottom pb-3 mb-4 gap-3">
      <div>
        <div className="text-muted small">{eyebrow}</div>
        <h3 className="mb-0">{title}</h3>
      </div>
      {actions}
    </div>
    {children}
  </Container>
);

const Loading = () => <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>;

const ErrorBox = ({ error }) => error ? <Alert variant="danger">{error}</Alert> : null;

const PaginationControls = ({ page, totalPage, onChange }) => {
  if (totalPage <= 1) return null;
  const windowSize = 5;
  const start = Math.max(0, Math.min(page - 2, totalPage - windowSize));
  const end = Math.min(totalPage, start + windowSize);
  return <div className="pagination-controls d-flex justify-content-center mt-3"><Pagination size="sm" className="mb-0"><Pagination.First disabled={page === 0} onClick={() => onChange(0)} /><Pagination.Prev disabled={page === 0} onClick={() => onChange(page - 1)} />{start > 0 && <Pagination.Ellipsis onClick={() => onChange(start - 1)} />}{Array.from({ length: end - start }, (_, index) => { const pageNumber = start + index; return <Pagination.Item key={pageNumber} active={pageNumber === page} onClick={() => onChange(pageNumber)}>{pageNumber + 1}</Pagination.Item>; })}{end < totalPage && <Pagination.Ellipsis onClick={() => onChange(end)} />}<Pagination.Next disabled={page >= totalPage - 1} onClick={() => onChange(page + 1)} /><Pagination.Last disabled={page >= totalPage - 1} onClick={() => onChange(totalPage - 1)} /></Pagination></div>;
};

const SimpleMasterPage = ({ title, resource, savePath, updatePath, deletePath, columns, fields }) => {
  const { call } = usePosApi();
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({});
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [page, setPage] = useState(0);
  const [totalPage, setTotalPage] = useState(1);
  const reset = (open = true) => {
    const initial = {};
    fields.forEach((field) => { initial[field.key] = field.defaultValue ?? (field.type === "checkbox" ? true : ""); });
    setSelected(null);
    setForm(initial);
    setShowForm(open);
  };
  const toggleForm = () => reset(!showForm);
  const load = async (nextPage = page, nextSearch = search) => {
    setLoading(true);
    try { const response = await call(resource, { search: nextSearch, page: nextPage, limit: 25 }); const result = pageData(response); setRows(result.rows); setPage(result.page); setTotalPage(result.totalPage); setError(""); } catch (e) { setError(e.response?.data?.message || e.message); } finally { setLoading(false); }
  };
  useEffect(() => { reset(false); load(); }, []);
  const submit = async (event) => {
    event.preventDefault();
    try {
      const body = { ...form };
      fields.filter((field) => field.type === "number").forEach((field) => { body[field.key] = body[field.key] === "" ? 0 : Number(body[field.key]); });
      if (selected) await call(`${updatePath}/${selected.id}`, body); else await call(savePath, body);
      reset(false); await load(page);
    } catch (e) { setError(e.response?.data?.message || e.message); }
  };
  const edit = (row) => { setSelected(row); setForm({ ...row }); setShowForm(true); };
  const remove = async (row) => {
    if (!window.confirm(`Hapus ${row.name}?`)) return;
    try { await call(`${deletePath}/${row.id}`); if (selected?.id === row.id) reset(false); await load(page); } catch (e) { setError(e.response?.data?.message || e.message); }
  };
  return <PageFrame eyebrow="POS Back Office" title={title} actions={<Button variant="primary" onClick={toggleForm}>{showForm ? "Tutup" : "+ Tambah"}</Button>}>
    <Row className="g-4">
      <Col lg={showForm ? 7 : 12}>
        <Card className="border-0 shadow-sm">
          <Card.Body className="pos-list-card-body">
            <div className="d-flex gap-2 mb-3"><Form.Control placeholder="Search" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load(0)} /><Button onClick={() => load(0)}>Cari</Button></div>
            <ErrorBox error={error} />
            {loading ? <Loading /> : <><div className="table-responsive"><Table hover className="align-middle"><thead className="table-light"><tr>{columns.map((column) => <th key={column.key}>{column.label}</th>)}<th /></tr></thead><tbody>{rows.map((row) => <tr key={row.id}>{columns.map((column) => <td key={column.key}>{column.render ? column.render(row) : row[column.key]}</td>)}<td className="text-end text-nowrap"><Button size="sm" variant="outline-primary" onClick={() => edit(row)}>Ubah</Button>{" "}<Button size="sm" variant="outline-danger" onClick={() => remove(row)}>Hapus</Button></td></tr>)}{!rows.length && <tr><td colSpan={columns.length + 1} className="text-center text-muted py-4">Belum ada data</td></tr>}</tbody></Table></div><PaginationControls page={page} totalPage={totalPage} onChange={load} /></>}
          </Card.Body>
        </Card>
      </Col>
      {showForm && <Col lg={5}><Card className="border-0 shadow-sm"><Card.Header className="bg-white fw-semibold">{selected ? "Ubah" : "Tambah"} {title}</Card.Header><Card.Body><Form onSubmit={submit}>{fields.map((field) => <Form.Group className="mb-3" key={field.key}><Form.Label>{field.label}</Form.Label>{field.type === "checkbox" ? <Form.Check type="switch" label={form[field.key] ? "Aktif" : "Nonaktif"} checked={Boolean(form[field.key])} onChange={(e) => setForm({ ...form, [field.key]: e.target.checked })} /> : field.type === "select" ? <Form.Select value={form[field.key] ?? ""} onChange={(e) => setForm({ ...form, [field.key]: e.target.value })}>{field.options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</Form.Select> : field.type === "number" ? <NumericInput required={field.required} step={field.step} value={form[field.key] ?? ""} onChange={(value) => setForm({ ...form, [field.key]: value })} /> : <Form.Control required={field.required} type={field.type || "text"} step={field.step} value={form[field.key] ?? ""} onChange={(e) => setForm({ ...form, [field.key]: e.target.value })} />}</Form.Group>)}<div className="d-flex gap-2"><Button type="submit">Simpan</Button><Button type="button" variant="outline-secondary" onClick={() => reset(false)}>Batal</Button></div></Form></Card.Body></Card></Col>}
    </Row>
  </PageFrame>;
};

export const PosBackOfficeDashboard = () => {
  const { call } = usePosApi();
  const [data, setData] = useState(null);
  const { user } = useContext(AuthContext);
  const [date, setDate] = useState(() => new Date().toLocaleDateString("en-CA"));
  const [tab, setTab] = useState("summary");
  const [error, setError] = useState("");
  useEffect(() => { let active = true; setData(null); call("/posDashboard", { date }).then(value => { if (active) { setData(value); setError(""); } }).catch(e => { if (active) setError(e.response?.data?.message || e.message); }); return () => { active = false; }; }, [date]);
  const rows = data?.topItems || [];
  const colors = ["#3366cc", "#dc3912", "#ff9900", "#109618", "#990099", "#0099c6", "#dd4477", "#66aa00"];
  const categories = [...new Set([...(data?.categories || []), ...rows.map(row => row.category)])];
  const changeDate = days => { const next = new Date(date + "T12:00:00"); next.setDate(next.getDate() + days); setDate(next.toLocaleDateString("en-CA")); };
  const chart = (values, labels, area = false) => {
    const max = Math.max(1, ...values), height = 220;
    const points = values.map((v, i) => `${55 + i * 530 / Math.max(1, values.length - 1)},${height - 30 - v / max * 155}`);
    return <svg viewBox="0 0 600 230" role="img" aria-label={area ? "Hourly gross sales amount" : "Sales chart"}>{[0, 1, 2, 3, 4].map(i => <g key={i}><line x1="55" x2="585" y1={35 + i * 39} y2={35 + i * 39} stroke="#dedede" /><text x="48" y={39 + i * 39} textAnchor="end">{number(max * (4 - i) / 4, 0)}</text></g>)}{area ? <polygon points={`55,190 ${points.join(" ")} 585,190`} fill="#3498db" /> : values.map((v, i) => <rect key={i} x={60 + i * 520 / Math.max(values.length, 1)} y={190 - v / max * 155} width={Math.min(35, 400 / Math.max(values.length, 1))} height={v / max * 155} fill="#3498db"><title>{labels[i]}: {number(v)}</title></rect>)}{labels.map((label, i) => <text key={i} x={60 + i * 520 / Math.max(labels.length - (area ? 1 : 0), 1)} y="210" textAnchor="middle">{label.length > 13 ? label.slice(0, 11) + "…" : label}</text>)}</svg>;
  };
  const pie = field => {
    const totals = categories.map(category => rows.filter(r => r.category === category).reduce((sum, r) => sum + Number(r[field] || 0), 0));
    const total = totals.reduce((a, b) => a + b, 0);
    let cursor = 0;
    const gradient = totals.map((v, i) => { const start = cursor; cursor += total ? v / total * 100 : 0; return `${colors[i % colors.length]} ${start}% ${cursor}%`; }).join(", ");
    return <div className="pos-dashboard-pie-wrap"><div className="pos-dashboard-pie" style={{ background: total ? `conic-gradient(${gradient})` : "#eee" }}>{!total && <span>No data</span>}</div><div>{categories.map((name, i) => <div key={name} className="mb-1"><i style={{ background: colors[i % colors.length] }} />{name}{total > 0 && ` (${number(totals[i] / total * 100, 1)}%)`}</div>)}</div></div>;
  };
  const section = title => <h5 className="pos-dashboard-section">{title}</h5>;
  return <PageFrame eyebrow="POS Back Office" title="Dashboard" className="pos-dashboard-page"><div className="pos-dashboard-content">
    <Nav variant="tabs" activeKey={tab} onSelect={setTab} className="mb-3"><Nav.Link eventKey="summary">Summary</Nav.Link><Nav.Link eventKey="comparison">Outlet Comparison</Nav.Link></Nav>
    <div className="d-flex gap-3 mb-4 align-items-center flex-wrap"><div className="pos-dashboard-branch">▣ {user?.cabang?.namaCabang || user?.namaCabang || "Cabang " + branchId(user)}</div><div className="d-flex"><Button variant="outline-secondary" onClick={() => changeDate(-1)}>‹</Button><Form.Control aria-label="Dashboard date" type="date" value={date} onChange={e => e.target.value && setDate(e.target.value)} /><Button variant="outline-secondary" onClick={() => changeDate(1)}>›</Button></div></div>
    <ErrorBox error={error} />
    {tab === "comparison" ? <><h5 className="pos-dashboard-section">OUTLET COMPARISON</h5><p className="text-muted small">Data cabang aktif pada tanggal yang dipilih.</p><Table bordered responsive><thead><tr><th>Outlet</th><th>Transactions</th><th>Gross Sales</th><th>Net Sales</th></tr></thead><tbody><tr><td>{user?.cabang?.namaCabang || "Cabang " + branchId(user)}</td><td>{data?.orderCount || 0}</td><td>{money(data?.grossSales)}</td><td>{money(data?.netSales)}</td></tr></tbody></Table></> : <>
    {section("SALES SUMMARY")}
    <Row className="g-3 mb-3">{[["GROSS SALES", money(data?.grossSales)], ["NET SALES", money(data?.netSales)], ["GROSS PROFIT", "—"], ["TRANSACTIONS", number(data?.orderCount)], ["AVERAGE SALE PER TRANSACTION", money(data?.orderCount ? data.netSales / data.orderCount : 0)], ["GROSS MARGIN", "—"]].map(([label, value]) => <Col md={4} key={label}><div className="pos-dashboard-panel pos-dashboard-metric"><div>{label}</div><strong>{value}</strong>{["GROSS PROFIT", "GROSS MARGIN"].includes(label) && <small>HPP POS belum tersedia</small>}</div></Col>)}</Row>
    <Row className="g-3 mb-4"><Col md={4}><div className="pos-dashboard-panel"><h6>DAY OF THE WEEK GROSS SALES AMOUNT</h6>{chart(["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((_, i) => new Date(date + "T12:00:00").getDay() === i ? Number(data?.grossSales || 0) : 0), ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"])}</div></Col><Col md={8}><div className="pos-dashboard-panel"><h6>HOURLY GROSS SALES AMOUNT</h6>{chart(data?.hourly || Array(24).fill(0), Array.from({ length: 24 }, (_, i) => String(i)), true)}</div></Col></Row>
    {section("ITEM SUMMARY")}<div className="pos-dashboard-panel mb-3"><h6>TOP ITEMS</h6><div className="pos-dashboard-top-items"><Table striped size="sm"><thead><tr><th>Item</th><th>Item Sold</th><th>Gross Sales</th><th>Net Sales</th><th>Gross Profit</th></tr></thead><tbody>{rows.map((r, i) => <tr key={i}><td>{r.name}</td><td>{number(r.quantity, 3)}</td><td>{money(r.grossSales)}</td><td>{money(r.netSales)}</td><td>—</td></tr>)}{!rows.length && <tr><td colSpan={5} className="text-center py-4 text-muted">{data ? "No sales for this date" : "Loading…"}</td></tr>}</tbody></Table></div></div>
    <Row className="g-3 mb-3"><Col md={6}><div className="pos-dashboard-panel"><h6>CATEGORY BY VOLUME</h6>{pie("quantity")}</div></Col><Col md={6}><div className="pos-dashboard-panel"><h6>CATEGORY BY SALES</h6>{pie("grossSales")}</div></Col></Row>
    <div className="pos-dashboard-panel"><h6>TOP ITEMS BY CATEGORY</h6><Row className="g-4">{categories.map(category => { const top = rows.filter(r => r.category === category).slice(0, 5); return <Col md={3} sm={6} key={category}><div className="small mb-2">{category}</div>{chart(top.length ? top.map(r => Number(r.quantity)) : [0], top.length ? top.map(r => r.name) : ["No sales"])}</Col>; })}</Row></div>
    </>}
  </div></PageFrame>;
};

export const PosCategories = () => <SimpleMasterPage title="Categories" resource="/posCategories" savePath="/savePosCategory" updatePath="/updatePosCategory" deletePath="/deletePosCategory" columns={[{ key: "name", label: "Category Name" }, { key: "sortOrder", label: "Sort" }, { key: "isActive", label: "Status", render: (r) => r.isActive ? <Badge bg="success">Active</Badge> : <Badge bg="secondary">Inactive</Badge> }]} fields={[{ key: "name", label: "Category name", required: true }, { key: "sortOrder", label: "Sort order", type: "number", defaultValue: 0 }, { key: "isActive", label: "Status", type: "checkbox", defaultValue: true }]} />;

export const PosSalesTypes = () => <SimpleMasterPage title="Sales Type" resource="/posSalesTypes" savePath="/savePosSalesType" updatePath="/updatePosSalesType" deletePath="/deletePosSalesType" columns={[{ key: "name", label: "Name" }, { key: "gratuityApplied", label: "Gratuity Applied", render: (row) => row.gratuityApplied ? "1 gratuity" : "0 gratuity" }, { key: "priceRule", label: "Price Rule" }, { key: "isActive", label: "Status", render: (row) => row.isActive ? <Badge bg="success">Active</Badge> : <Badge bg="secondary">Inactive</Badge> }]} fields={[{ key: "name", label: "Sales type name", required: true }, { key: "priceRule", label: "Price rule", defaultValue: "FIXED" }, { key: "gratuityApplied", label: "Gratuity applied", type: "checkbox", defaultValue: false }, { key: "isActive", label: "Status", type: "checkbox", defaultValue: true }]} />;

export const PosIngredientCategories = () => <SimpleMasterPage title="Ingredient Categories" resource="/posIngredientCategories" savePath="/savePosIngredientCategory" updatePath="/updatePosIngredientCategory" deletePath="/deletePosIngredientCategory" columns={[{ key: "name", label: "Name" }, { key: "isActive", label: "Status", render: (r) => r.isActive ? <Badge bg="success">Active</Badge> : <Badge bg="secondary">Inactive</Badge> }]} fields={[{ key: "name", label: "Category name", required: true }, { key: "isActive", label: "Status", type: "checkbox", defaultValue: true }]} />;

export const PosItems = () => {
  const { call } = usePosApi();
  const [rows, setRows] = useState([]); const [categories, setCategories] = useState([]); const [selected, setSelected] = useState(null); const [search, setSearch] = useState(""); const [error, setError] = useState(""); const [loading, setLoading] = useState(true); const [page, setPage] = useState(0); const [totalPage, setTotalPage] = useState(1);
  const blank = () => ({ categoryId: "", name: "", description: "", image: "", price: 0, notes: "", isActive: true });
  const [form, setForm] = useState(blank()); const [showForm, setShowForm] = useState(false);
  const load = async (nextPage = page, nextSearch = search) => { setLoading(true); try { const [items, bootstrap] = await Promise.all([call("/posItems", { search: nextSearch, page: nextPage, limit: 25 }), call("/posBootstrap")]); const result = pageData(items); setRows(result.rows); setPage(result.page); setTotalPage(result.totalPage); setCategories(bootstrap.categories || []); setError(""); } catch (e) { setError(e.response?.data?.message || e.message); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);
  const save = async (e) => { e.preventDefault(); try { const body = { ...form, categoryId: form.categoryId || null, price: Number(form.price || 0) }; if (selected) await call(`/updatePosItem/${selected.id}`, body); else await call("/savePosItem", body); setSelected(null); setForm(blank()); setShowForm(false); await load(page); } catch (x) { setError(x.response?.data?.message || x.message); } };
  const selectItem = (row) => { setSelected(row); setShowForm(true); setForm({ ...blank(), ...row }); };
  const toggleForm = () => { if (showForm) { setSelected(null); setForm(blank()); } setShowForm(!showForm); };
  return <PageFrame eyebrow="POS Back Office / Library" title="Item Library" actions={<Button onClick={toggleForm}>{showForm ? "Tutup" : "+ Create Item"}</Button>}><Row className="g-4"><Col lg={showForm ? 7 : 12}><Card className="border-0 shadow-sm"><Card.Body><div className="d-flex gap-2 mb-3"><Form.Control placeholder="Search item" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load(0)} /><Button onClick={() => load(0)}>Cari</Button></div><ErrorBox error={error} />{loading ? <Loading /> : <><div className="table-responsive"><Table hover className="pos-items-table"><thead className="table-light"><tr><th>Item name</th><th>Category</th><th>Price</th><th>Status</th></tr></thead><tbody>{rows.map((row) => <tr key={row.id} role="button" tabIndex={0} onClick={() => selectItem(row)} onKeyDown={(e) => e.key === "Enter" && selectItem(row)}><td>{row.name}</td><td>{row.category?.name || "-"}</td><td>{money(row.price)}</td><td>{row.isActive ? "Active" : "Inactive"}</td></tr>)}{!rows.length && <tr><td colSpan="4" className="text-center text-muted">Belum ada data</td></tr>}</tbody></Table></div><PaginationControls page={page} totalPage={totalPage} onChange={load} /></>}</Card.Body></Card></Col>{showForm && <Col lg={5}><Card className="border-0 shadow-sm"><Card.Header className="bg-white fw-semibold">{selected ? "Ubah" : "Create"} Item</Card.Header><Card.Body><Form onSubmit={save}><Form.Group className="mb-3"><Form.Label>Category</Form.Label><Form.Select required value={form.categoryId} onChange={(e) => setForm({ ...form, categoryId: e.target.value })}><option value="">Pilih category</option>{categories.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}</Form.Select></Form.Group><Form.Group className="mb-3"><Form.Label>Item name</Form.Label><Form.Control required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Form.Group><Form.Group className="mb-3"><Form.Label>Description</Form.Label><Form.Control as="textarea" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Form.Group><Form.Group className="mb-3"><Form.Label>Price</Form.Label><NumericInput decimalDigits={2} step="0.01" value={form.price} onChange={(value) => setForm({ ...form, price: value })} /></Form.Group><Form.Group className="mb-3"><Form.Label>Notes</Form.Label><Form.Control value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Form.Group><Form.Check className="mb-3" type="switch" label={form.isActive ? "Active" : "Inactive"} checked={Boolean(form.isActive)} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} /><Button type="submit">Simpan</Button></Form></Card.Body></Card></Col>}</Row></PageFrame>;
};


export const PosIngredientLibrary = () => {
  const { call, user } = usePosApi();
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [ingredientOptions, setIngredientOptions] = useState([]);
  const [form, setForm] = useState({});
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showRecipe, setShowRecipe] = useState(false);
  const [showIngredientPicker, setShowIngredientPicker] = useState(false);
  const [recipeLines, setRecipeLines] = useState([]);
  const [recipeInput, setRecipeInput] = useState({ ingredientId: "", quantity: 0, unit: "", averageCost: 0 });
  const [ingredientSearch, setIngredientSearch] = useState("");
  const [selectedRawIngredient, setSelectedRawIngredient] = useState(null);
  const [error, setError] = useState("");
  const [recipeError, setRecipeError] = useState("");
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [totalPage, setTotalPage] = useState(1);

  const blank = (ingredientType = "RAW") => ({
    categoryId: "", name: "", ingredientType, unit: "GRAM", image: "", inventoryTracked: true,
    notes: "", isActive: true, recipeOutputQuantity: 1,
  });

  const load = async (nextPage = page, nextSearch = search) => {
    setLoading(true);
    try {
      const [ingredients, bootstrap] = await Promise.all([
        call("/posIngredients", { cabangId: branchId(user), search: nextSearch, page: nextPage, limit: 25 }),
        call("/posBootstrap"),
      ]);
      const result = pageData(ingredients);
      setRows(result.rows);
      setPage(result.page);
      setTotalPage(result.totalPage);
      setCategories(bootstrap.ingredientCategories || []);
      setIngredientOptions(bootstrap.ingredients || []);
      setError("");
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { setForm(blank()); load(); }, []);

  const startCreate = (ingredientType) => {
    setSelected(null);
    setForm(blank(ingredientType));
    setRecipeLines([]);
    setRecipeInput({ ingredientId: "", quantity: 0, unit: "", averageCost: 0 });
    setRecipeError("");
    setShowForm(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const body = { ...form, categoryId: form.categoryId || null };
      const saved = selected
        ? await call(`/updatePosIngredient/${selected.id}`, body)
        : await call("/savePosIngredient", body);
      if (form.ingredientType === "SEMI_FINISHED") {
        const recipeBody = {
          recipeType: "SEMI_FINISHED",
          ingredientId: saved.id,
          outputQuantity: form.recipeOutputQuantity || 1,
          outputUnit: form.unit || "GRAM",
          lines: recipeLines.map((line) => ({ ingredientId: line.ingredientId, quantity: line.quantity, unit: line.unit, averageCost: line.averageCost })),
          isActive: form.isActive,
        };
        if (selected?.recipe?.id) await call(`/updatePosRecipe/${selected.recipe.id}`, recipeBody);
        else if (recipeLines.length) await call("/savePosRecipe", recipeBody);
      }
      setSelected(null);
      setForm(blank());
      setRecipeLines([]);
      setShowForm(false);
      await load(page);
    } catch (x) {
      setError(x.response?.data?.message || x.message);
    }
  };

  const edit = (row) => {
    const recipe = row.recipe;
    setSelected(row);
    setShowForm(true);
    setForm({ ...blank(row.ingredientType), ...row, recipeOutputQuantity: recipe?.outputQuantity || 1 });
    setRecipeLines((recipe?.lines || []).map((line) => ({
      ingredientId: line.ingredientId,
      ingredientName: line.ingredient?.name || "Ingredient",
      quantity: line.quantity,
      unit: line.unit,
      averageCost: line.averageCost,
    })));
  };

  const remove = async (row) => {
    if (!window.confirm(`Hapus ${row.name}?`)) return;
    try { await call(`/deletePosIngredient/${row.id}`); await load(page); } catch (x) { setError(x.response?.data?.message || x.message); }
  };

  const toggleForm = () => {
    if (showForm) {
      setSelected(null);
      setForm(blank());
      setRecipeLines([]);
    }
    setShowForm(!showForm);
  };

  const openIngredientPicker = () => {
    setIngredientSearch("");
    setSelectedRawIngredient(null);
    setRecipeInput({ ingredientId: "", quantity: 0, unit: "", averageCost: 0 });
    setRecipeError("");
    setShowRecipe(false);
    setShowIngredientPicker(true);
  };

  const selectRawIngredient = (ingredient) => {
    setSelectedRawIngredient(ingredient);
    setRecipeInput({ ingredientId: ingredient.id, quantity: 0, unit: ingredient.unit || "", averageCost: ingredient.averageCost ?? 0 });
    setRecipeError("");
  };

  const addRecipeLine = () => {
    const ingredient = ingredientOptions.find((row) => String(row.id) === String(recipeInput.ingredientId));
    if (!ingredient) return setRecipeError("Ingredient wajib dipilih");
    if (Number(recipeInput.quantity || 0) <= 0) return setRecipeError("Quantity recipe harus lebih besar dari 0");
    setRecipeLines([...recipeLines, { ...recipeInput, ingredientName: ingredient.name }]);
    setRecipeInput({ ingredientId: "", quantity: 0, unit: "", averageCost: 0 });
    setRecipeError("");
  };

  const addSelectedRawIngredient = () => {
    if (!selectedRawIngredient) return setRecipeError("Raw ingredient wajib dipilih");
    addRecipeLine();
    if (Number(recipeInput.quantity || 0) > 0) {
      setShowIngredientPicker(false);
      setShowRecipe(true);
      setSelectedRawIngredient(null);
      setIngredientSearch("");
    }
  };

  const rawIngredients = ingredientOptions.filter((row) => row.ingredientType === "RAW");
  const filteredRawIngredients = rawIngredients.filter((row) => row.name.toLowerCase().includes(ingredientSearch.trim().toLowerCase()));
  const formTitle = form.ingredientType === "SEMI_FINISHED" ? "Semi-Finished Ingredient" : "Raw Ingredient";

return <PageFrame eyebrow="POS Back Office / Ingredient" title="Ingredient Library" actions={showForm ? <Button onClick={toggleForm}>Tutup</Button> : <DropdownButton title="+ Create Ingredient" variant="primary" align="end"><Dropdown.Item onClick={() => startCreate("SEMI_FINISHED")}>Semi-Finished Ingredient</Dropdown.Item><Dropdown.Item onClick={() => startCreate("RAW")}>Raw Ingredient</Dropdown.Item></DropdownButton>}><Row className="g-4"><Col lg={showForm ? 7 : 12}><Card className="border-0 shadow-sm"><Card.Body><div className="d-flex gap-2 mb-3"><Form.Control placeholder="Search ingredient" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load(0)} /><Button onClick={() => load(0)}>Cari</Button></div><ErrorBox error={error} />{loading ? <Loading /> : <><div className="table-responsive"><Table hover><thead className="table-light"><tr><th>Name</th><th>Type</th><th>In Stock</th><th>Unit</th><th>Category</th><th>Stock Alert</th><th /></tr></thead><tbody>{rows.map((row) => <tr key={row.id}><td>{row.name}</td><td>{row.ingredientType}</td><td>{number(row.inStock, 3)}</td><td>{row.unit}</td><td>{row.category?.name || "-"}</td><td>{Number(row.inStock) <= 0 ? <span className="text-danger">Out</span> : ""}</td><td className="text-end"><Button size="sm" variant="outline-primary" onClick={() => edit(row)}>Ubah</Button>{" "}<Button size="sm" variant="outline-danger" onClick={() => remove(row)}>Hapus</Button></td></tr>)}{!rows.length && <tr><td colSpan="7" className="text-center text-muted">Belum ada data</td></tr>}</tbody></Table></div><PaginationControls page={page} totalPage={totalPage} onChange={load} /></>}</Card.Body></Card></Col>{showForm && <Col lg={5}><Card className="border-0 shadow-sm"><Card.Header className="bg-white fw-semibold">{selected ? "Ubah" : "Create"} {formTitle}</Card.Header><Card.Body><Form onSubmit={save}><Form.Group className="mb-3"><Form.Label>Ingredient name</Form.Label><Form.Control required value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Form.Group><Form.Group className="mb-3"><Form.Label>Ingredient category</Form.Label><Form.Select value={form.categoryId || ""} onChange={(e) => setForm({ ...form, categoryId: e.target.value })}><option value="">Pilih category</option>{categories.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}</Form.Select></Form.Group><Form.Group className="mb-3"><Form.Label>Unit of Measurements</Form.Label><Form.Control required value={form.unit || ""} onChange={(e) => setForm({ ...form, unit: e.target.value.toUpperCase() })} /></Form.Group>{form.ingredientType === "SEMI_FINISHED" && <div className="border rounded p-3 mb-3"><div className="fw-semibold mb-1">Recipe</div><div className="small text-muted mb-2">Attach the raw ingredient components.</div><Button type="button" className="w-100" onClick={() => { setRecipeError(""); setShowRecipe(true); }}>Manage Recipe</Button>{recipeLines.length > 0 && <div className="small mt-2">{recipeLines.length} ingredient recipe tersimpan</div>}</div>}<Button type="submit">Simpan</Button></Form></Card.Body></Card></Col>}</Row><Modal show={showRecipe} onHide={() => setShowRecipe(false)} size="lg" centered><Modal.Header closeButton><Modal.Title>Manage Recipe</Modal.Title></Modal.Header><Modal.Body><Form.Group className="mb-3"><Form.Label>Recipe for</Form.Label><Form.Control readOnly value={form.name || ""} /></Form.Group><Row className="mb-3"><Col><Form.Label>This recipe is to create</Form.Label><NumericInput decimalDigits={3} step="0.001" value={form.recipeOutputQuantity} onChange={(value) => setForm({ ...form, recipeOutputQuantity: value })} /></Col><Col><Form.Label>Unit</Form.Label><Form.Control readOnly value={form.unit || ""} /></Col></Row><div className="fw-semibold mb-2">Ingredients Needed</div><Button type="button" className="w-100 mb-3" onClick={openIngredientPicker}>Add Raw Ingredient</Button><ErrorBox error={recipeError} />{recipeLines.length > 0 && <Table bordered size="sm"><thead className="table-light"><tr><th>Ingredient</th><th>Quantity</th><th>Unit</th><th>Avg Cost</th><th /></tr></thead><tbody>{recipeLines.map((line, index) => <tr key={`${line.ingredientId}-${index}`}><td>{line.ingredientName}</td><td>{number(line.quantity, 3)}</td><td>{line.unit}</td><td>{money(line.averageCost)}</td><td><Button type="button" size="sm" variant="outline-danger" onClick={() => setRecipeLines(recipeLines.filter((_, lineIndex) => lineIndex !== index))}>Hapus</Button></td></tr>)}</tbody></Table>}</Modal.Body><Modal.Footer><Button variant="secondary" onClick={() => setShowRecipe(false)}>Selesai</Button></Modal.Footer></Modal><Modal show={showIngredientPicker} onHide={() => { setShowIngredientPicker(false); setShowRecipe(true); setSelectedRawIngredient(null); }} size="lg" centered><Modal.Header closeButton><Modal.Title>Add Raw Ingredient</Modal.Title></Modal.Header><Modal.Body><Form.Control autoFocus placeholder="Search ingredients" value={ingredientSearch} onChange={(e) => setIngredientSearch(e.target.value)} />{selectedRawIngredient ? <div className="mt-3"><div className="fw-semibold mb-2">{selectedRawIngredient.name}</div><Row className="g-2 align-items-end"><Col md={5}><Form.Label>Ingredient</Form.Label><Form.Control readOnly value={selectedRawIngredient.name} /></Col><Col md={2}><Form.Label>Quantity</Form.Label><NumericInput decimalDigits={3} step="0.001" value={recipeInput.quantity} onChange={(value) => setRecipeInput({ ...recipeInput, quantity: value })} /></Col><Col md={2}><Form.Label>Unit</Form.Label><Form.Control readOnly value={recipeInput.unit} /></Col><Col md={2}><Form.Label>Avg Cost</Form.Label><Form.Control readOnly value={money(recipeInput.averageCost)} /></Col><Col md={1}><Button type="button" variant="link" className="p-0" onClick={() => setSelectedRawIngredient(null)}>Ganti</Button></Col></Row></div> : <div className="border rounded mt-3" style={{ maxHeight: "320px", overflowY: "auto" }}>{filteredRawIngredients.map((row) => <Button key={row.id} type="button" variant="light" className="w-100 text-start border-0 border-bottom rounded-0 py-3" onClick={() => selectRawIngredient(row)}>{row.name}</Button>)}{!filteredRawIngredients.length && <div className="text-center text-muted py-4">Ingredient tidak ditemukan</div>}</div>}<ErrorBox error={recipeError} /></Modal.Body><Modal.Footer><Button variant="secondary" onClick={() => { setShowIngredientPicker(false); setShowRecipe(true); setSelectedRawIngredient(null); }}>Batal</Button><Button type="button" disabled={!selectedRawIngredient} onClick={addSelectedRawIngredient}>Add</Button></Modal.Footer></Modal></PageFrame>;
};

export const PosRecipes = () => {
  const { call } = usePosApi();
  const [recipes, setRecipes] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [items, setItems] = useState([]);
  const [type, setType] = useState("ITEM");
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [totalPage, setTotalPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [picker, setPicker] = useState({ kind: "", lineIndex: null });
  const [pickerSearch, setPickerSearch] = useState("");
  const blankLine = () => ({ ingredientId: "", quantity: 0, unit: "GRAM", averageCost: 0 });
  const blank = () => ({ recipeType: "ITEM", itemId: "", ingredientId: "", outputQuantity: 1, outputUnit: "PCS", lines: [blankLine()], isActive: true });
  const [form, setForm] = useState(blank());
  const detailReadOnly = Boolean(selected);
  const recipeEditorFormRef = useRef(null);

  const load = async (nextPage = page, nextType = type, nextSearch = search) => {
    try {
      setError("");
      const [recipeResponse, bootstrap] = await Promise.all([
        call("/posRecipes", { page: nextPage, limit: 25, recipeType: nextType, search: nextSearch }),
        call("/posBootstrap"),
      ]);
      const result = pageData(recipeResponse);
      setRecipes(result.rows);
      setPage(result.page);
      setTotalPage(result.totalPage);
      setTotalRows(result.totalRows);
      setIngredients(bootstrap.ingredients || []);
      setItems(bootstrap.items || []);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    }
  };

  useEffect(() => { load(0, type, search); }, []);

  useEffect(() => {
    if (showForm && recipeEditorFormRef.current) recipeEditorFormRef.current.scrollTop = 0;
  }, [selected, type, showForm]);

  const ingredientById = useMemo(() => new Map(ingredients.map((row) => [String(row.id), row])), [ingredients]);
  const totals = useMemo(() => {
    const total = form.lines.reduce((sum, line) => sum + Number(line.quantity || 0) * Number(line.averageCost || 0), 0);
    return { total, average: Number(form.outputQuantity || 0) ? total / Number(form.outputQuantity) : 0 };
  }, [form.lines, form.outputQuantity]);
  const unitLabel = (unit) => ({ GRAM: "gram (g)", ML: "millilitre (ml)", PCS: "pieces (pcs)", BTR: "butir (btr)", BTL: "botol (btl)" }[String(unit || "").toUpperCase()] || unit || "-");
  const recipeTarget = (row) => row.item?.name || row.outputIngredient?.name || "-";
  const ingredientCount = (row) => `${(row.lines || []).length} ingredients`;
  const stockAlert = (row) => {
    const outCount = (row.lines || []).filter((line) => Number(ingredientById.get(String(line.ingredientId))?.availableQuantity ?? 0) <= 0).length;
    return outCount > 0 ? <span className="text-danger">{outCount} Out</span> : null;
  };
  const targetOptions = type === "ITEM" ? items : ingredients.filter((row) => row.ingredientType === "SEMI_FINISHED");
  const pickerOptions = (picker.kind === "target" ? targetOptions : ingredients).filter((row) => row.name.toLowerCase().includes(pickerSearch.trim().toLowerCase()));
  const selectedTarget = targetOptions.find((row) => String(row.id) === String(type === "ITEM" ? form.itemId : form.ingredientId));

  const openPicker = (kind, lineIndex = null) => {
    setPicker({ kind, lineIndex });
    setPickerSearch("");
  };
  const closePicker = () => {
    setPicker({ kind: "", lineIndex: null });
    setPickerSearch("");
  };
  const selectPickerOption = (row) => {
    if (picker.kind === "target") {
      const key = type === "ITEM" ? "itemId" : "ingredientId";
      setForm((current) => ({ ...current, [key]: row.id }));
    } else if (picker.kind === "ingredient") {
      setForm((current) => ({
        ...current,
        lines: current.lines.map((line, index) => index === picker.lineIndex
          ? { ...line, ingredientId: row.id, unit: row.unit || line.unit, averageCost: row.averageCost ?? line.averageCost }
          : line),
      }));
    }
    closePicker();
  };
  const openPickerFromKeyboard = (event, kind, lineIndex = null) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    openPicker(kind, lineIndex);
  };

  const updateLine = (index, key, value) => {
    const lines = form.lines.map((line, lineIndex) => {
      if (lineIndex !== index) return line;
      if (key === "ingredientId") {
        const ingredient = ingredients.find((row) => String(row.id) === String(value));
        return { ...line, ingredientId: value, unit: ingredient?.unit || line.unit, averageCost: ingredient?.averageCost ?? line.averageCost };
      }
      return { ...line, [key]: value };
    });
    setForm({ ...form, lines });
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const body = { ...form, itemId: form.recipeType === "ITEM" ? form.itemId || null : null, ingredientId: form.recipeType === "SEMI_FINISHED" ? form.ingredientId || null : null };
      if (selected) await call(`/updatePosRecipe/${selected.id}`, body);
      else await call("/savePosRecipe", body);
      setSelected(null);
      setForm(blank());
      setShowForm(false);
      await load(page, type, search);
    } catch (x) {
      setError(x.response?.data?.message || x.message);
    }
  };

  const edit = (row) => {
    setSelected(row);
    setShowForm(true);
    setType(row.recipeType);
    setForm({ ...blank(), ...row, lines: (row.lines || []).map((line) => ({ ingredientId: line.ingredientId, quantity: line.quantity, unit: line.unit, averageCost: line.averageCost })) });
  };
  const toggleForm = () => {
    if (showForm) {
      setSelected(null);
      setForm(blank());
    }
    setShowForm(!showForm);
  };
  const selectType = (key) => {
    setType(key);
    setSearch("");
    setSelected(null);
    setForm({ ...blank(), recipeType: key });
    setShowForm(false);
    load(0, key, "");
  };

  return <PageFrame eyebrow="POS Back Office / Ingredient" title="Recipes" actions={<Button onClick={toggleForm}>{showForm ? "Tutup" : "+ Create Recipe"}</Button>}>
    <Nav variant="tabs" activeKey={type} onSelect={selectType} className="mb-3">
      <Nav.Item><Nav.Link eventKey="ITEM">Item</Nav.Link></Nav.Item>
      <Nav.Item><Nav.Link eventKey="SEMI_FINISHED">Semi-Finished Ingredient</Nav.Link></Nav.Item>
    </Nav>
    <Row className="g-4">
      <Col lg={showForm ? 7 : 12}>
        <Card className="border-0 shadow-sm">
          <Card.Body>
            <div className="d-flex gap-2 align-items-center mb-3">
              <Form.Control placeholder="Search" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load(0, type, search)} />
              <Button onClick={() => load(0, type, search)}>Cari</Button>
              <div className="border rounded px-3 py-2 text-nowrap small">Total: {totalRows}</div>
            </div>
            <ErrorBox error={error} />
            <div className="table-responsive">
              <Table hover>
                <thead className="table-light">
                  {type === "ITEM" ? <tr><th>Item name</th><th>Variant</th><th>Ingredient</th><th>Ingredient Stock Alert</th></tr> : <tr><th>Semi-Finished Ingredient</th><th>Ingredient Category</th><th>Recipe Outcome</th><th>Ingredient</th><th>Ingredient Stock Alert</th></tr>}
                </thead>
                <tbody>
                  {recipes.map((row) => <tr key={row.id} role="button" tabIndex={0} onClick={() => edit(row)} onKeyDown={(e) => e.key === "Enter" && edit(row)}>
                    {type === "ITEM" ? <><td>{recipeTarget(row)}</td><td>-</td><td>{ingredientCount(row)}</td><td>{stockAlert(row)}</td></> : <><td>{recipeTarget(row)}</td><td>{row.outputIngredient?.category?.name || "-"}</td><td>{number(row.outputQuantity, 3)} {unitLabel(row.outputUnit)}</td><td>{ingredientCount(row)}</td><td>{stockAlert(row)}</td></>}
                  </tr>)}
                  {!recipes.length && <tr><td colSpan={type === "ITEM" ? 4 : 5} className="text-center text-muted">Belum ada recipe</td></tr>}
                </tbody>
              </Table>
            </div>
            <PaginationControls page={page} totalPage={totalPage} onChange={(nextPage) => load(nextPage, type, search)} />
          </Card.Body>
        </Card>
      </Col>
      {showForm && (
        <Col lg={5}>
          <Card className="border-0 shadow-sm pos-recipe-editor">
            <Card.Header className="bg-white fw-semibold pos-recipe-editor-header">{selected ? "Ubah" : "Create"} Recipe</Card.Header>
            <Card.Body className="pos-recipe-editor-body">
              <Form ref={recipeEditorFormRef} className="pos-recipe-editor-form" onSubmit={save}>
                <Form.Group className="pos-recipe-field mb-2">
                  <Form.Label className="pos-recipe-label text-uppercase text-muted">{type === "ITEM" ? "Choose item" : "Recipe for"}</Form.Label>
                  <div className="position-relative pos-recipe-picker-wrap">
                    <Form.Control size="sm" className="pos-recipe-picker-input" required disabled={detailReadOnly} readOnly value={selectedTarget?.name || ""} placeholder="Pilih target" onClick={() => !detailReadOnly && openPicker("target")} onKeyDown={(event) => !detailReadOnly && openPickerFromKeyboard(event, "target")} />
                    {!detailReadOnly && <span className="pos-recipe-picker-caret" aria-hidden="true">⌄</span>}
                  </div>
                </Form.Group>
                {type === "ITEM" && <Form.Group className="pos-recipe-field mb-2">
                  <Form.Label className="pos-recipe-label text-uppercase text-muted">Choose variant</Form.Label>
                  <Form.Control size="sm" disabled readOnly value="No Variant" />
                </Form.Group>}
                <Row className="g-2">
                  <Col>
                    <Form.Group className="pos-recipe-field mb-2">
                      <Form.Label className="pos-recipe-label">This recipe is to create</Form.Label>
                      <NumericInput size="sm" disabled={detailReadOnly} readOnly={detailReadOnly} required decimalDigits={6} step="0.000001" value={form.outputQuantity} onChange={(value) => setForm({ ...form, outputQuantity: value })} />
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group className="pos-recipe-field mb-2">
                      <Form.Label className="pos-recipe-label">Output unit</Form.Label>
                      <Form.Control size="sm" disabled={detailReadOnly} readOnly={detailReadOnly} value={form.outputUnit} onChange={(e) => setForm({ ...form, outputUnit: e.target.value.toUpperCase() })} />
                    </Form.Group>
                  </Col>
                </Row>
                <div className="pos-recipe-section-title text-uppercase text-muted fw-semibold border-bottom pb-1 mb-2">RECIPE</div>
                <div className="table-responsive pos-recipe-lines-wrap">
                  <Table bordered size="sm" className="mb-2 align-middle pos-recipe-lines-table">
                    <thead className="table-light">
                      <tr><th>Ingredient</th><th>Quantity</th><th>Unit</th><th>Avg Cost</th><th /></tr>
                    </thead>
                    <tbody>
                      {form.lines.map((line, index) => <tr key={index}>
                        <td>
                          <div className="position-relative pos-recipe-picker-wrap">
                            <Form.Control size="sm" className="pos-recipe-control pos-recipe-picker-input" required disabled={detailReadOnly} readOnly value={ingredientById.get(String(line.ingredientId))?.name || ""} placeholder="Pilih ingredient" onClick={() => !detailReadOnly && openPicker("ingredient", index)} onKeyDown={(event) => !detailReadOnly && openPickerFromKeyboard(event, "ingredient", index)} />
                            {!detailReadOnly && <span className="pos-recipe-picker-caret" aria-hidden="true">⌄</span>}
                          </div>
                        </td>
                        <td><NumericInput size="sm" className="pos-recipe-control" disabled={detailReadOnly} readOnly={detailReadOnly} decimalDigits={6} step="0.000001" value={line.quantity} onChange={(value) => updateLine(index, "quantity", value)} /></td>
                        <td><Form.Control size="sm" className="pos-recipe-control" disabled={detailReadOnly} readOnly={detailReadOnly} placeholder="Unit" value={line.unit} onChange={(e) => updateLine(index, "unit", e.target.value.toUpperCase())} /></td>
                        <td><NumericInput size="sm" className="pos-recipe-control" disabled={detailReadOnly} readOnly={detailReadOnly} decimalDigits={6} step="0.000001" value={line.averageCost} onChange={(value) => updateLine(index, "averageCost", value)} /></td>
                        <td><Button type="button" size="sm" variant="outline-danger" className="pos-recipe-remove px-1" onClick={() => setForm({ ...form, lines: form.lines.filter((_, lineIndex) => lineIndex !== index) })}>×</Button></td>
                      </tr>)}
                    </tbody>
                  </Table>
                </div>
                <div className="pos-recipe-total d-flex justify-content-between align-items-center mb-2">
                  <span className="text-uppercase text-muted">TOTAL AVG COST</span>
                  <strong>{money(totals.total)}</strong>
                </div>
                {type === "SEMI_FINISHED" && <div className="pos-recipe-total d-flex justify-content-between align-items-center mb-2">
                  <strong className="text-uppercase">AVG COST (PER PIECE)</strong>
                  <strong>{money(totals.average)}</strong>
                </div>}
                <Button type="button" size="sm" className="w-100 mb-2" disabled={detailReadOnly} onClick={() => setForm({ ...form, lines: [...form.lines, blankLine()] })}>{type === "SEMI_FINISHED" ? "Add Raw Ingredient" : "Add Ingredient"}</Button>
                <div className="pos-recipe-section-title text-uppercase text-muted fw-semibold border-bottom pb-1 mb-2">INVENTORY</div>
                <div className="table-responsive pos-recipe-inventory-wrap">
                  <Table bordered size="sm" className="mb-0 align-middle pos-recipe-inventory-table">
                    <thead className="table-light"><tr><th>Ingredient</th><th>In Stock</th><th>Unit</th></tr></thead>
                    <tbody>
                      {form.lines.map((line, index) => {
                        const ingredient = ingredientById.get(String(line.ingredientId));
                        return <tr key={`inventory-${line.ingredientId || "line"}-${index}`}><td>{ingredient?.name || "-"}</td><td className={Number(ingredient?.availableQuantity ?? 0) <= 0 ? "text-danger" : ""}>{number(ingredient?.availableQuantity ?? 0, 3)}</td><td>{unitLabel(ingredient?.unit || line.unit)}</td></tr>;
                      })}
                    </tbody>
                  </Table>
                </div>
                <Button type="submit" className="mt-3 w-100">Simpan Recipe</Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      )}
    </Row>
    <Modal show={Boolean(picker.kind)} onHide={closePicker} centered size="lg" className="pos-recipe-picker-modal">
      <Modal.Header closeButton>
        <Modal.Title>{picker.kind === "target" ? (type === "ITEM" ? "Choose Item" : "Choose Semi-Finished Ingredient") : "Add Ingredient"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Control autoFocus size="sm" placeholder={picker.kind === "target" ? "Search item" : "Search ingredients"} value={pickerSearch} onChange={(event) => setPickerSearch(event.target.value)} />
        <div className="pos-recipe-picker-list border rounded mt-2">
          {pickerOptions.map((row) => <button type="button" key={row.id} className="pos-recipe-picker-option" onClick={() => selectPickerOption(row)}>
            <span className="pos-recipe-picker-avatar" aria-hidden="true" />
            <span className="text-truncate">{row.name}</span>
            <small className="text-muted ms-auto">{row.unit || row.category?.name || ""}</small>
          </button>)}
          {!pickerOptions.length && <div className="text-center text-muted py-4 small">Data tidak ditemukan</div>}
        </div>
      </Modal.Body>
      <Modal.Footer><Button size="sm" variant="secondary" onClick={closePicker}>Batal</Button></Modal.Footer>
    </Modal>
  </PageFrame>;
};

const PosTabLayout = ({ title, children, actions, className = "" }) => <PageFrame eyebrow="POS Tab" title={title} actions={actions} className={className}>{children}</PageFrame>;

export const PosTabLibrary = ({ custom = false }) => {
  const { call, user } = usePosApi();
  const [data, setData] = useState({ categories: [], items: [], salesTypes: [], paymentMethods: [] });
  const [libraryView, setLibraryView] = useState("categories");
  const [categoryId, setCategoryId] = useState("");
  const [salesTypeId, setSalesTypeId] = useState("");
  const [paymentMethodId, setPaymentMethodId] = useState("");
  const [search, setSearch] = useState("");
  const [cart, setCart] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [detailQuantity, setDetailQuantity] = useState(1);
  const [detailSalesTypeId, setDetailSalesTypeId] = useState("");
  const [detailNotes, setDetailNotes] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [libraryPage, setLibraryPage] = useState(0);
  const [libraryTotalPage, setLibraryTotalPage] = useState(1);
  const [libraryTotalRows, setLibraryTotalRows] = useState(0);
  const [customAmount, setCustomAmount] = useState("");
  const appendCustomDigit = (digit) => setCustomAmount((current) => (current + digit).replace(/^0+/, "").slice(0, 12));
  const addCustomAmount = () => {
    if (Number(customAmount) <= 0) return;
    setCart((current) => [...current, { cartKey: `custom-${Date.now()}-${current.length}`, itemId: null, nameSnapshot: "Custom Amount", quantity: 1, unitPrice: Number(customAmount), salesTypeId: salesTypeId || null, notes: "" }]);
    setCustomAmount("");
  };

  const loadLibrary = async (nextPage = libraryPage, nextSearch = search, nextCategoryId = categoryId) => {
    try {
      setLoading(true);
      const response = await call("/posTabItems", { page: nextPage, limit: 25, search: nextSearch, categoryId: nextCategoryId });
      const result = pageData(response);
      setData((current) => ({ ...current, items: result.rows }));
      setLibraryPage(result.page);
      setLibraryTotalPage(result.totalPage);
      setLibraryTotalRows(result.totalRows);
      setError("");
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    call("/posBootstrap")
      .then((response) => {
        setData({
          categories: response.categories || [],
          items: [],
          salesTypes: response.salesTypes || [],
          paymentMethods: response.paymentMethods || [],
        });
        const defaultSalesType = response.salesTypes?.[0]?.id ? String(response.salesTypes[0].id) : "";
        const defaultPayment = response.paymentMethods?.[0]?.id ? String(response.paymentMethods[0].id) : "";
        setSalesTypeId((current) => current || defaultSalesType);
        setPaymentMethodId((current) => current || defaultPayment);
        setLoading(false);
      })
      .catch((e) => {
        setError(e.response?.data?.message || e.message);
        setLoading(false);
      });
  }, []);

  const priceFor = (item) => Number(item.price || 0);
  const selectedSalesType = detailSalesTypeId || salesTypeId;
  const selectedSalesTypeName = data.salesTypes.find((type) => String(type.id) === String(salesTypeId))?.name || "Dine in";
  const visible = data.items;
  const activeCategory = data.categories.find((row) => String(row.id) === String(categoryId));
  const categoryRows = useMemo(() => {
    const term = search.trim().toLocaleLowerCase("id-ID");
    const rows = [{ id: "", name: "All Items", isAllItems: true }, ...data.categories];
    return term ? rows.filter((row) => row.name.toLocaleLowerCase("id-ID").includes(term)) : rows;
  }, [data.categories, search]);
  const initials = (value) => String(value || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
  const openCategory = (nextCategoryId) => {
    setCategoryId(String(nextCategoryId || ""));
    setLibraryView("items");
    setSearch("");
    loadLibrary(0, "", String(nextCategoryId || ""));
  };
  const backToCategories = () => {
    setLibraryView("categories");
    setCategoryId("");
    setSearch("");
    setData((current) => ({ ...current, items: [] }));
    setLibraryPage(0);
    setLibraryTotalPage(1);
    setLibraryTotalRows(0);
  };
  const submitLibrarySearch = () => {
    if (libraryView === "items") loadLibrary(0, search, categoryId);
  };
  const total = cart.reduce((sum, item) => sum + Number(item.quantity || 0) * Number(item.unitPrice || 0), 0);
  const openItem = (item) => {
    setSelectedItem(item);
    setDetailQuantity(1);
    setDetailSalesTypeId(salesTypeId);
    setDetailNotes("");
  };
  const saveItemToCart = () => {
    if (!selectedItem || Number(detailQuantity) <= 0) return;
    const unitPrice = priceFor(selectedItem, selectedSalesType);
    const existing = cart.find((row) => row.itemId === selectedItem.id && String(row.salesTypeId || "") === String(selectedSalesType || ""));
    if (existing) {
      setCart(cart.map((row) => row === existing ? { ...row, quantity: Number(row.quantity) + Number(detailQuantity), notes: detailNotes || row.notes } : row));
    } else {
      setCart([...cart, { itemId: selectedItem.id, nameSnapshot: selectedItem.name, quantity: Number(detailQuantity), unitPrice, salesTypeId: selectedSalesType || null, variantText: "", notes: detailNotes }]);
    }
    setSelectedItem(null);
  };
  const saveOrder = async (status) => {
    if (!cart.length || saving) return;
    setSaving(true);
    try {
      let shiftId = null;
      if (status === "COMPLETED") {
        const shifts = pageData(await call("/posShifts", { cabangId: branchId(user), status: "OPEN", page: 0, limit: 1 }));
        shiftId = shifts.rows[0]?.id || null;
        let options;
        try { options = JSON.parse(localStorage.getItem(`posShiftOptions:${branchId(user)}`)); } catch { options = null; }
        if (!shiftId && options?.automatic) {
          const shift = await call("/savePosShift", { cabangId: branchId(user), startingCash: Number(options.startingCash || 0) });
          shiftId = shift.id;
        }
      }
      const payments = status === "COMPLETED" && paymentMethodId
        ? [{ paymentMethodId: Number(paymentMethodId), amount: total, tendered: total, changeAmount: 0 }]
        : [];
      await call("/savePosOrder", { cabangId: branchId(user), shiftId, salesTypeId: salesTypeId || null, status, items: cart, payments });
      setCart([]);
      setError("");
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setSaving(false);
    }
  };

  return <PosTabLayout title={custom ? "Custom" : "Library"} className={`pos-tab-library-page ${custom ? "pos-tab-custom-page" : ""}`}>
    <ErrorBox error={error} />
    {loading ? <Loading /> : <div className="pos-tab-library-shell">
      <Row className="g-0">
        <Col lg={7}>
          {custom ? <Card className="pos-custom-keypad-card">
            <div className="pos-custom-amount" role="status" aria-label="Custom amount">{money(customAmount)}</div>
            <div className="pos-custom-keypad">
              {["1", "2", "3", "0", "4", "5", "6", "00", "7", "8", "9"].map((digit) => <button type="button" key={digit} className="pos-custom-key" onClick={() => appendCustomDigit(digit)}>{digit}</button>)}
              <button type="button" className="pos-custom-key pos-custom-add" aria-label="Tambah Custom Amount" disabled={!Number(customAmount)} onClick={addCustomAmount}>+</button>
              <button type="button" className="pos-custom-key pos-custom-clear" onClick={() => setCustomAmount("")}>C</button>
              <button type="button" className="pos-custom-key pos-custom-delete" onClick={() => setCustomAmount((current) => current.slice(0, -1))}>Del</button>
            </div>
          </Card> : <Card className="pos-tab-library-card">
            <Card.Body className="p-0">
              <div className="pos-tab-library-searchbar">
                <div className="pos-tab-search-control">
                  <Form.Control
                    placeholder="Search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && submitLibrarySearch()}
                  />
                  <Button type="button" variant="link" className="pos-tab-search-button" aria-label="Cari" onClick={submitLibrarySearch}>⌕</Button>
                </div>
              </div>
              {libraryView === "categories" ? <>
                <div className="pos-tab-library-title border-bottom"><span>Library</span><Button type="button" variant="link" className="pos-tab-library-edit" aria-label="Edit Library">✎</Button></div>
                <div className="pos-tab-library-list">
                  {categoryRows.map((category) => <button
                    type="button"
                    className="pos-tab-library-row"
                    key={category.isAllItems ? "all-items" : category.id}
                    onClick={() => openCategory(category.id)}
                  >
                    <span className="pos-tab-library-avatar">{initials(category.name)}</span>
                    <span className="flex-grow-1 text-start">{category.name}</span>
                    <span className="pos-tab-library-chevron" aria-hidden="true">›</span>
                  </button>)}
                  {!categoryRows.length && <div className="text-center text-muted py-5">Kategori tidak ditemukan</div>}
                </div>
              </> : <>
                <div className="pos-tab-library-subheader border-bottom">
                  <Button type="button" variant="link" className="pos-tab-library-back" onClick={backToCategories} aria-label="Kembali ke kategori">‹</Button>
                  <div className="text-center flex-grow-1">
                    <div className="fw-semibold">{activeCategory?.name || "All Items"}</div>
                    <div className="small text-muted">{libraryTotalRows} Items</div>
                  </div>
                  <Button type="button" variant="link" className="pos-tab-library-edit" aria-label="Edit Library">✎</Button>
                </div>
                <div className="pos-tab-library-list pos-tab-item-list">
                  {visible.map((item) => <button type="button" className="pos-tab-library-row" key={item.id} onClick={() => openItem(item)}>
                    <span className="pos-tab-library-avatar bg-secondary-subtle text-secondary">{initials(item.name)}</span>
                    <span className="flex-grow-1 text-start">{item.name}</span>
                    <span className="fw-semibold">{money(priceFor(item))}</span>
                  </button>)}
                  {!visible.length && <div className="text-center text-muted py-5">Belum ada item</div>}
                </div>
                <PaginationControls page={libraryPage} totalPage={libraryTotalPage} onChange={(nextPage) => loadLibrary(nextPage, search, categoryId)} />
              </>}
            </Card.Body>
          </Card>}
        </Col>
        <Col lg={5}>
          <Card className="pos-tab-cart-card">
            <Card.Header className="pos-tab-cart-header">
              <div className="pos-tab-billing-label"><span className="pos-tab-billing-icon" aria-hidden="true">☷</span><span>Billing List</span></div>
              <Button type="button" variant="link" className="pos-tab-add-customer">+ Add Customer</Button>
            </Card.Header>
            <Card.Body className="p-0">
              <div className="pos-tab-cart-sales-type">
                <Dropdown className="w-100">
                  <Dropdown.Toggle variant="link" className="pos-tab-sales-type-toggle w-100">{selectedSalesTypeName}<span aria-hidden="true">⌄</span></Dropdown.Toggle>
                  <Dropdown.Menu className="w-100">{data.salesTypes.map((type) => <Dropdown.Item key={type.id} active={String(type.id) === String(salesTypeId)} onClick={() => setSalesTypeId(String(type.id))}>{type.name}</Dropdown.Item>)}</Dropdown.Menu>
                </Dropdown>
              </div>
              <div className="pos-tab-cart-items">{cart.map((item) => <div className="pos-tab-cart-item" key={item.cartKey || `${item.itemId}-${item.salesTypeId}`}><div className="d-flex justify-content-between align-items-start"><div className="fw-semibold">{item.nameSnapshot}</div><button type="button" className="btn btn-sm btn-link text-danger p-0 pos-tab-cart-remove" aria-label={`Hapus ${item.nameSnapshot}`} onClick={() => setCart(cart.filter((row) => row !== item))}>×</button></div><div className="small text-muted">{item.quantity > 1 ? `${item.quantity} x ` : ""}{money(Number(item.quantity || 0) * Number(item.unitPrice || 0))}</div>{item.notes && <div className="small text-muted mt-1">{item.notes}</div>}</div>)}{!cart.length && (custom ? <div className="d-flex justify-content-between small text-muted"><span>Custom Amount</span><span>{money(customAmount)}</span></div> : <div className="text-muted text-center py-5">No Items</div>)}</div>
              <div className="pos-tab-cart-summary">
                <div className="d-flex justify-content-between"><span>Subtotal:</span><span>{money(total)}</span></div>
                <div className="d-flex justify-content-between fw-semibold fs-5 mt-2"><span>Total:</span><span>{money(total)}</span></div>
                <div className="d-flex justify-content-between text-muted small mt-2"><span>Rounding:</span><span>{money(0)}</span></div>
                <Button type="button" variant="link" className="pos-tab-clear-sale" disabled={!cart.length} onClick={() => setCart([])}>Clear sale</Button>
                <Form.Select className="pos-tab-payment-select" value={paymentMethodId} onChange={(e) => setPaymentMethodId(e.target.value)} aria-label="Payment method"><option value="">Payment method (optional)</option>{data.paymentMethods.map((method) => <option key={method.id} value={method.id}>{method.name}</option>)}</Form.Select>
              </div>
            </Card.Body>
            <Card.Footer className="pos-tab-cart-footer p-0">
              <div className="d-flex"><Button variant="link" className="pos-tab-cart-footer-button flex-fill" disabled={!cart.length || saving} onClick={() => saveOrder("DRAFT")}>Save Bill</Button><Button variant="link" className="pos-tab-cart-footer-button flex-fill" disabled={!cart.length}>Print Bill</Button></div>
              <div className="d-flex"><Button variant="link" className="pos-tab-split-bill" disabled={!cart.length}><span aria-hidden="true">▱</span><small>Split Bill</small></Button><Button variant="primary" className="pos-tab-charge flex-grow-1" disabled={!cart.length || saving} onClick={() => saveOrder("COMPLETED")}>{saving ? "Menyimpan..." : `Charge ${money(total)}`}</Button></div>
            </Card.Footer>
          </Card>
        </Col>
      </Row>
    </div>}
    <Modal show={Boolean(selectedItem)} onHide={() => setSelectedItem(null)} centered size="xl" dialogClassName="pos-tab-item-modal">
      <Modal.Header className="pos-tab-item-modal-header">
        <Button variant="link" className="text-decoration-none" onClick={() => setSelectedItem(null)}>Cancel</Button>
        <Modal.Title className="text-center">
          <div>{selectedItem?.name}</div>
          <div className="small text-muted">{selectedItem ? money(priceFor(selectedItem, selectedSalesType)) : ""}</div>
        </Modal.Title>
        <Button variant="primary" onClick={saveItemToCart}>Save</Button>
      </Modal.Header>
      <Modal.Body className="pos-tab-item-modal-body">
        <section className="pos-tab-detail-section">
          <div className="pos-tab-detail-label">QUANTITY</div>
          <div className="d-flex gap-2">
            <NumericInput className="flex-grow-1 text-end" decimalDigits={0} min="1" step="1" value={detailQuantity} onChange={(value) => setDetailQuantity(value)} />
            <div className="btn-group flex-grow-1">
              <Button variant="outline-primary" onClick={() => setDetailQuantity((value) => Math.max(1, Number(value || 1) - 1))}>−</Button>
              <Button variant="outline-primary" onClick={() => setDetailQuantity((value) => Number(value || 1) + 1)}>+</Button>
            </div>
          </div>
        </section>
        <section className="pos-tab-detail-section">
          <div className="pos-tab-detail-label">SALES TYPE <span> | CHOOSE ONE</span></div>
          <div className="row g-2">
            {data.salesTypes.map((type) => <div className="col-md-6" key={type.id}><Button className="w-100 py-3" variant={String(selectedSalesType) === String(type.id) ? "primary" : "outline-primary"} onClick={() => setDetailSalesTypeId(String(type.id))}>{type.name}</Button></div>)}
          </div>
        </section>
        <section className="pos-tab-detail-section">
          <div className="pos-tab-detail-label">NOTES</div>
          <Form.Control as="textarea" rows={5} placeholder="Description" value={detailNotes} onChange={(e) => setDetailNotes(e.target.value)} />
        </section>
      </Modal.Body>
    </Modal>
  </PosTabLayout>;
};

export const PosTabCustom = () => <PosTabLibrary custom />;

export const PosTabActivity = () => {
  const { call, user } = usePosApi();
  const detailRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [page, setPage] = useState(0);
  const [totalPage, setTotalPage] = useState(1);
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [salesTypes, setSalesTypes] = useState([]);
  useEffect(() => { if (detailRef.current) detailRef.current.scrollTop = 0; }, [selected?.id]);
  const load = async (nextPage = 0, term = search) => {
    setLoading(true);
    try {
      const response = await call("/posOrders", { cabangId: branchId(user), page: nextPage, limit: 25, search: term });
      const result = pageData(response);
      setRows(result.rows); setPage(result.page); setTotalPage(result.totalPage);
      setSelected(result.rows[0] || null); setError("");
    } catch (e) { setError(e.response?.data?.message || e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    load(0, "");
    call("/posBootstrap").then((data) => setSalesTypes(data.salesTypes || [])).catch(() => {});
  }, []);
  const refund = async () => {
    if (!selected || !window.confirm(`Catat refund penuh untuk ${selected.receiptNo || selected.orderNo}?`)) return;
    try {
      await call("/savePosRefund", { cabangId: branchId(user), orderId: selected.id, amount: selected.total, reason: "Refund POS" });
      alert("Refund berhasil dicatat");
    } catch (e) { setError(e.response?.data?.message || e.message); }
  };
  const date = (value) => new Date(value).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" });
  const time = (value) => new Date(value).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" });
  const payments = selected?.payments || [];
  const tendered = payments.reduce((sum, p) => sum + Number(p.tendered || 0), 0);
  const change = payments.reduce((sum, p) => sum + Number(p.changeAmount || 0), 0);
  return <PosTabLayout title="Activity" className="pos-activity-page">
    <ErrorBox error={error} />
    <div className="pos-activity-workspace">
      <section className="pos-activity-list" aria-label="Daftar nota">
        <Form onSubmit={(e) => { e.preventDefault(); load(0); }} className="pos-activity-search">
          <Form.Control size="sm" placeholder="Receipt or Invoice Number" aria-label="Cari nota" value={search} onChange={(e) => setSearch(e.target.value)} />
          <Button size="sm" type="submit">Cari</Button>
        </Form>
        <div className="pos-activity-orders">
          {loading ? <Loading /> : rows.map((row, index) => <React.Fragment key={row.id}>
            {(index === 0 || date(row.orderDate) !== date(rows[index - 1].orderDate)) && <div className="pos-activity-date">{date(row.orderDate)}</div>}
            <button type="button" className={`pos-activity-order ${selected?.id === row.id ? "active" : ""}`} onClick={() => setSelected(row)}>
              <div className="d-flex justify-content-between"><span>{money(row.total)}</span><small>{time(row.orderDate)}</small></div>
              <div className="pos-activity-preview">{(row.items || []).map((item) => `${item.nameSnapshot}${Number(item.quantity) > 1 ? ` x ${number(item.quantity, 3)}` : ""}`).join(", ") || row.orderNo}</div>
            </button>
          </React.Fragment>)}
          {!loading && !rows.length && <div className="p-4 text-muted text-center">Belum ada nota</div>}
        </div>
        <PaginationControls page={page} totalPage={totalPage} onChange={(next) => load(next)} />
      </section>
      <section ref={detailRef} className="pos-activity-detail" aria-label="Detail nota">
        {selected ? <>
          <div className="d-flex gap-2 mb-3">
            <Button variant="outline-primary" className="flex-fill" disabled title="Pengiriman receipt belum dikonfigurasi">Send Receipt</Button>
            <Button variant="outline-primary" className="flex-fill" disabled={selected.status !== "COMPLETED" && selected.status !== "CHARGED"} onClick={refund}>Issue Refund</Button>
          </div>
          <div className="pos-activity-section-title">DETAILS</div>
          <div className="pos-receipt-row"><span>Payment Method</span><span>{payments.map((p) => p.paymentMethod?.name || "-").join(", ") || "Belum ada pembayaran"}</span></div>
          <div className="pos-receipt-row"><span>Receipt Number</span><span>{selected.receiptNo || selected.orderNo}</span></div>
          <div className="pos-receipt-row"><span>Time of Purchase</span><span>{date(selected.orderDate)} • {time(selected.orderDate)}</span></div>
          <div className="pos-activity-section-title mt-3">ITEMS</div>
          <div className="text-center small text-muted">{salesTypes.find((type) => String(type.id) === String(selected.salesTypeId))?.name || "-"}</div>
          {(selected.items || []).map((item) => <div key={item.id} className="pos-receipt-item">
            <span className="pos-receipt-avatar">{item.nameSnapshot?.slice(0, 2)}</span>
            <div className="flex-grow-1">{item.nameSnapshot}{item.variantText && <div className="text-muted small">{item.variantText}</div>}{item.notes && <div className="text-muted small">{item.notes}</div>}</div>
            <span>x {number(item.quantity, 3)}</span><span className="pos-receipt-price">{money(item.lineTotal)}</span>
          </div>)}
          {[["Subtotal", selected.subtotal], ["PPN", selected.tax], ["Gratuity", selected.gratuity], ["Rounding", selected.rounding], ["Total", selected.total], ["Tendered", tendered], ["Change", change]].map(([label, value]) => <div key={label} className={`pos-receipt-row ${label === "Total" ? "fw-semibold" : ""}`}><span>{label}</span><span>{money(value)}</span></div>)}
        </> : <div className="text-muted text-center py-5">Pilih nota untuk melihat detail</div>}
      </section>
    </div>
  </PosTabLayout>;
};

export const PosTabInventory = () => {
  const { call, user } = usePosApi();
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPage, setTotalPage] = useState(1);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const listRef = useRef(null);
  const load = async (nextPage = 0, term = appliedSearch) => {
    setLoading(true);
    try {
      const response = await call("/posInventory", { cabangId: branchId(user), page: nextPage, limit: 25, search: term });
      const result = pageData(response);
      setRows(result.rows); setPage(result.page); setTotalPage(result.totalPage); setAppliedSearch(term); setError("");
      if (listRef.current) listRef.current.scrollTop = 0;
    } catch (e) { setError(e.response?.data?.message || e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(0, ""); }, []);
  const unitLabel = (unit) => ({ GRAM: "gram (g)", ML: "millilitre (ml)", PCS: "pieces (pcs)", BTL: "botol (btl)", BTR: "butir (btr)" }[String(unit || "").toUpperCase()] || unit || "-");
  return <PosTabLayout title="Inventory" className="pos-inventory-page">
    <ErrorBox error={error} />
    <div className="pos-inventory-workspace">
      <Form className="pos-inventory-toolbar" onSubmit={(e) => { e.preventDefault(); load(0, search.trim()); }}>
        <Form.Select size="sm" aria-label="Inventory type" value="ingredients" onChange={() => {}}><option value="ingredients">Ingredients</option></Form.Select>
        <div className="pos-inventory-search">
          <Form.Control size="sm" placeholder="Search" aria-label="Search ingredient" value={search} onChange={(e) => setSearch(e.target.value)} />
          <Button type="submit" variant="link" aria-label="Cari ingredient">⌕</Button>
        </div>
      </Form>
      <div className="pos-inventory-title">Inventory</div>
      <div className="pos-inventory-scroll" ref={listRef}>
        <Table hover className="pos-inventory-table">
          <colgroup><col style={{ width: "38%" }} /><col style={{ width: "24%" }} /><col style={{ width: "24%" }} /><col style={{ width: "14%" }} /></colgroup>
          <thead><tr><th>INGREDIENT NAME</th><th>IN STOCK</th><th>UNIT</th><th>STOCK ALERT</th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={4}><Loading /></td></tr> : rows.map((row) => <tr key={row.id}>
              <td><div className="d-flex align-items-center gap-2"><span className="pos-inventory-avatar" aria-hidden="true">{(row.ingredient?.name || "?").slice(0, 2)}</span><span>{row.ingredient?.name || "-"}</span></div></td>
              <td>{number(row.quantity, 3)}</td><td>{unitLabel(row.unit || row.ingredient?.unit)}</td>
              <td>{Number(row.quantity) <= 0 ? <span className="text-danger">Out</span> : ""}</td>
            </tr>)}
            {!loading && !rows.length && <tr><td colSpan={4} className="text-center text-muted py-4">Ingredient tidak ditemukan</td></tr>}
          </tbody>
        </Table>
      </div>
      <PaginationControls page={page} totalPage={totalPage} onChange={(next) => load(next)} />
    </div>
  </PosTabLayout>;
};

export const PosTabShift = () => {
  const { call, user } = usePosApi();
  const storageKey = `posShiftOptions:${branchId(user)}`;
  const [options, setOptions] = useState(() => { try { return JSON.parse(localStorage.getItem(storageKey)) || { automatic: false, startingCash: 0 }; } catch { return { automatic: false, startingCash: 0 }; } });
  const [view, setView] = useState("options");
  const [rows, setRows] = useState([]);
  const [current, setCurrent] = useState(null);
  const [selected, setSelected] = useState(null);
  const [startingCash, setStartingCash] = useState(options.startingCash);
  const [endingCash, setEndingCash] = useState(0);
  const [page, setPage] = useState(0);
  const [totalPage, setTotalPage] = useState(1);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [showEnd, setShowEnd] = useState(false);
  const load = async (nextPage = 0) => {
    try {
      const result = pageData(await call("/posShifts", { cabangId: branchId(user), page: nextPage, limit: 25, status: "CLOSED" }));
      const open = pageData(await call("/posShifts", { cabangId: branchId(user), page: 0, limit: 1, status: "OPEN" }));
      setRows(result.rows); setPage(result.page); setTotalPage(result.totalPage); setCurrent(open.rows[0] || null); setError("");
    } catch (e) { setError(e.response?.data?.message || e.message); }
  };
  useEffect(() => { load(0); }, []);
  const updateOptions = (next) => { setOptions(next); localStorage.setItem(storageKey, JSON.stringify(next)); };
  const start = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await call("/savePosShift", { cabangId: branchId(user), startingCash }); await load(); }
    catch (x) { setError(x.response?.data?.message || x.message); }
    finally { setBusy(false); }
  };
  const close = async (e) => {
    e.preventDefault(); if (!current) return; setBusy(true);
    try { await call(`/closePosShift/${current.id}`, { cabangId: branchId(user), endingCash }); setShowEnd(false); await load(); setView("history"); }
    catch (x) { setError(x.response?.data?.message || x.message); }
    finally { setBusy(false); }
  };
  const date = (value) => value ? new Date(value).toLocaleString("id-ID", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-";
  return <PosTabLayout title="Shift" className="pos-shift-page">
    <ErrorBox error={error} />
    <div className="pos-shift-workspace">
      <aside className="pos-shift-menu">
        <div className="pos-shift-heading">Shift</div>
        <div className="pos-shift-register" aria-hidden="true"><svg width="64" height="76" viewBox="0 0 64 76" fill="none" stroke="currentColor" strokeWidth="3"><rect x="15" y="3" width="34" height="25" rx="1"/><path d="M32 28v8M21 36h22v16H21zM21 52 10 62v10h44V62L43 52M16 66h32"/><text x="24" y="47" fill="currentColor" stroke="none" fontSize="10">Rp</text></svg></div>
        <div className="pos-shift-management">SHIFT MANAGEMENT</div>
        {[["options", "Shift Option"], ["current", "Current Shift"], ["history", "Shift History"]].map(([key, label]) => <button key={key} type="button" className={view === key ? "active" : ""} onClick={() => { setView(key); setSelected(null); setError(""); }}>{label}{key === "options" && <small>On</small>}</button>)}
      </aside>
      <section className="pos-shift-main">
        <div className="pos-shift-heading">{view === "options" ? "Automatic Shift" : view === "current" ? "Current Shift" : selected ? "Shift Report" : "Shift History"}</div>
        <div className="pos-shift-content">
          {view === "options" && <>
            <div className="pos-shift-field"><span>Start Shift Automatically</span><Form.Check type="switch" aria-label="Start Shift Automatically" checked={options.automatic} onChange={(e) => updateOptions({ ...options, automatic: e.target.checked })} /></div>
            <div className="pos-shift-field"><Form.Label className="mb-0" htmlFor="default-shift-cash">Default Starting Cash In Drawer</Form.Label><NumericInput id="default-shift-cash" decimalDigits={2} value={options.startingCash} onChange={(value) => { updateOptions({ ...options, startingCash: value }); setStartingCash(value); }} /></div>
            <p className="pos-shift-help">Shift enables you to track cash, card and other payment flow that goes in and out of your drawer.</p>
          </>}
          {view === "current" && (current ? <>
            <div className="pos-receipt-row"><span>Started At</span><span>{date(current.openedAt)}</span></div>
            <div className="pos-shift-field"><span>Starting Cash In Drawer</span><span>{money(current.startingCash)}</span></div>
            <Button className="w-100 pos-shift-primary" onClick={() => { setEndingCash(0); setShowEnd(true); }}>End Shift</Button>
          </> : <Form onSubmit={start}>
            <div className="pos-shift-field"><Form.Label className="mb-0" htmlFor="starting-shift-cash">Starting Cash In Drawer</Form.Label><NumericInput id="starting-shift-cash" decimalDigits={2} value={startingCash} onChange={setStartingCash} /></div>
            <Button className="w-100 pos-shift-primary" type="submit" disabled={busy}>{busy ? "Menyimpan..." : "Start Shift"}</Button>
          </Form>)}
          {view === "history" && (selected ? <>
            <Button variant="link" className="p-0 mb-3" onClick={() => setSelected(null)}>‹ Closed Shifts</Button>
            {[["Started At", date(selected.openedAt)], ["Ended At", date(selected.closedAt)], ["Starting Cash In Drawer", money(selected.startingCash)], ["Actual Cash In Drawer", money(selected.endingCash)]].map(([label, value]) => <div key={label} className="pos-receipt-row"><span>{label}</span><span>{value}</span></div>)}
          </> : <>
            <div className="pos-shift-history-title">Closed Shifts</div>
            {rows.map((row) => <button type="button" key={row.id} className="pos-shift-history-row" onClick={() => setSelected(row)}><span>{date(row.openedAt)} - {date(row.closedAt)}</span><span>›</span></button>)}
            {!rows.length && <div className="text-muted py-4 text-center">Belum ada shift yang ditutup</div>}
            <PaginationControls page={page} totalPage={totalPage} onChange={load} />
          </>)}
        </div>
      </section>
    </div>
    <Modal show={showEnd} onHide={() => !busy && setShowEnd(false)} centered>
      <Form onSubmit={close}><Modal.Header closeButton><Modal.Title>End Shift</Modal.Title></Modal.Header><Modal.Body><Form.Label>Actual Cash In Drawer</Form.Label><NumericInput decimalDigits={2} value={endingCash} onChange={setEndingCash} /></Modal.Body><Modal.Footer><Button variant="secondary" onClick={() => setShowEnd(false)}>Cancel</Button><Button type="submit" disabled={busy}>End Shift</Button></Modal.Footer></Form>
    </Modal>
  </PosTabLayout>;
};

export default PosBackOfficeDashboard;
